import React from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useSiteContext } from 'src/store';

type LanguageSelectorProps = {
  handleOptionClicked?: () => void;
};

export const LanguageSelectorMenu = (props: LanguageSelectorProps) => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { handleOptionClicked } = props;
  const { locales, pathname, asPath, query, locale } = router;

  const { languagesSupported } = useSiteContext();

  const selectedLanguageCode = languagesSupported?.find(
    (language) =>
      language.code.toLocaleLowerCase() === locale?.toLocaleLowerCase()
  )?.code;

  const languagesFiltered = languagesSupported?.filter((language) =>
    locales?.some(
      (l) => l.toLocaleLowerCase() === language.code.toLocaleLowerCase()
    )
  );

  const setCookie = (selectedLocale?: string) => {
    document.cookie = `NEXT_LOCALE=${selectedLocale}; max-age=31536000; path=/`;
  };

  const shouldShowLanguageSelector =
    languagesFiltered && languagesFiltered.length > 1;

  const handleChange = (
    event: SelectChangeEvent<typeof selectedLanguageCode>
  ) => {
    const {
      target: { value },
    } = event;

    const selectedLocale = locales?.find(
      (l) => l.toLocaleLowerCase() === value?.toLocaleLowerCase()
    );

    setCookie(selectedLocale);

    // change just the locale and maintain all other route information including href's query
    router.push({ pathname, query }, asPath, {
      locale: selectedLocale,
      shallow: false,
    });

    if (handleOptionClicked) {
      handleOptionClicked();
    }
  };

  return (
    <>
      {shouldShowLanguageSelector && (
        <>
          <FormControl sx={{ m: 1, minWidth: 120 }}>
            <InputLabel id="language-selector-label">
              {t('languageSelector.label')}
            </InputLabel>
            <Select
              labelId="language-selector-label"
              value={selectedLanguageCode}
              onChange={handleChange}
              label="select language"
              variant="outlined"
              data-testid="language-selector"
              size="small"
              autoWidth
              sx={{ maxHeight: '40px' }}
              MenuProps={{
                MenuListProps: {
                  // @ts-expect-error - limitations with mui props and data attributes
                  'data-testid': 'language-selector-list',
                },
              }}
            >
              {languagesFiltered?.map((language) => (
                <MenuItem value={language.code} key={language.code}>
                  {language.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
    </>
  );
};

export default LanguageSelectorMenu;
